import { HTMLAttributes } from 'react'
import { map } from 'lodash'
import Link from 'next/link'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'
import { isProtectedRoute } from '@/utils/path'

import { cn } from '@/lib/utils'

export type MenuProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    open: boolean
  }>

const Menu = ({ open = false, className, ...props }: MenuProps) => {
  const { menu } = useAppStore()

  return (
    <div
      inert={!open}
      className={cn(
        'grid',
        'transition-all duration-[400ms] ease-out-cubic',
        {
          'grid-rows-[0fr]': !open,
          'grid-rows-[1fr]': open
        },
        'focus-links-light',
        className
      )}
      {...props}
    >
      <div className="grid overflow-hidden">
        <div className="grid grid-cols-2 items-start gap-y-[5rem] py-[3rem] md:gap-y-[7rem] md:py-[7rem]">
          <div className={cn('col-span-1', 'flex md:justify-center')}>
            <ul className="grid w-full max-w-[20rem] gap-y-[2rem]">
              {menu
                ? map(Array.from({ length: 4 }), (_, index) => {
                    const menuItem = menu.main_menu_01?.menu[index]
                    return (
                      <li
                        key={`menu-item-${index}`}
                        className={cn(
                          'transition-all duration-300 ease-out-cubic',
                          'translate-y-[1rem] opacity-0',
                          {
                            'translate-y-0 opacity-100': open
                          }
                        )}
                        style={{ transitionDelay: `${open ? 0.1 + index * 0.05 : 0}s` }}
                      >
                        {menuItem ? (
                          <Link href={menuItem.url} className="-m-[1rem] p-[1rem]" scroll={false}>
                            <Typography variant="standard" className="capitalize">
                              {menuItem.title}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography variant="standard" className="capitalize">
                            &nbsp;
                          </Typography>
                        )}
                      </li>
                    )
                  })
                : null}
            </ul>
          </div>

          <div className={cn('col-span-1', 'flex md:justify-center')}>
            <ul className="grid w-full max-w-[20rem] gap-y-[2rem]">
              {menu
                ? map(Array.from({ length: 4 }), (_, index) => {
                    const menuItem = menu.main_menu_02?.menu[index]
                    return (
                      <li
                        key={`menu-item-${index}`}
                        className={cn(
                          'transition-all duration-300 ease-out-cubic',
                          'translate-y-[1rem] opacity-0',
                          {
                            'translate-y-0 opacity-100': open
                          }
                        )}
                        style={{ transitionDelay: `${open ? 0.2 + index * 0.05 : 0}s` }}
                      >
                        {menuItem ? (
                          <Link href={menuItem.url} className="-m-[1rem] p-[1rem]" scroll={false}>
                            <Typography variant="standard" className="capitalize">
                              {menuItem.title}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography variant="standard" className="capitalize">
                            &nbsp;
                          </Typography>
                        )}
                      </li>
                    )
                  })
                : null}
            </ul>
          </div>

          <div className={cn('col-span-1', 'flex md:justify-center')}>
            <ul className="grid w-full max-w-[20rem] items-start gap-y-[1.5rem]">
              {map(menu?.main_menu_03?.menu, ({ title, url }, index) => (
                <li
                  key={`menu-item-${index}`}
                  className={cn(
                    'transition-all duration-300 ease-out-cubic',
                    'translate-y-[1rem] opacity-0',
                    {
                      'translate-y-0 opacity-100': open
                    }
                  )}
                  style={{ transitionDelay: `${open ? 0.3 + index * 0.05 : 0}s` }}
                >
                  <Link href={url} className="-m-[1rem] p-[1rem]" scroll={false}>
                    <Typography variant="upper-small" className="font-bold uppercase">
                      {title}
                    </Typography>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className={cn('col-span-1', 'flex size-full md:justify-center')}>
            <ul className="grid w-full max-w-[20rem] items-start gap-y-[1.5rem]">
              {map(menu?.main_menu_04?.menu, ({ title, url }, index) => (
                <li
                  key={`menu-item-${index}`}
                  className={cn(
                    'transition-all duration-300 ease-out-cubic',
                    'translate-y-[1rem] opacity-0',
                    {
                      'translate-y-0 opacity-100': open
                    }
                  )}
                  style={{ transitionDelay: `${open ? 0.4 + index * 0.05 : 0}s` }}
                >
                  <Link
                    href={url}
                    className="-m-[1rem] p-[1rem]"
                    scroll={false}
                    prefetch={!isProtectedRoute(url)}
                  >
                    <Typography variant="upper-small" className="font-bold uppercase">
                      {title}
                    </Typography>
                  </Link>
                </li>
              ))}
              <li className="-ml-[1rem] mt-auto flex space-x-[0.75rem] md:space-x-[1.25rem]">
                {map(menu?.social_menu?.menu, ({ title, classes, url }, index) => (
                  <span
                    key={`menu-item-${index}`}
                    className={cn(
                      'flex',
                      'transition-all duration-300 ease-out-cubic',
                      'translate-x-[1rem] opacity-0',
                      {
                        'translate-x-0 opacity-100': open
                      }
                    )}
                    style={{ transitionDelay: `${open ? 0.4 + index * 0.05 : 0}s` }}
                  >
                    <Link href={url} scroll={false} className="-m-[0.5rem] p-[0.5rem]">
                      <span className="sr-only">{title}</span>
                      <SVGIcon symbol={classes} className="size-[2.4rem] shrink-0 fill-current" />
                    </Link>
                  </span>
                ))}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Menu
